import { getAuthHeaders, authedFetch } from './authService';
const HOSTNAME = process.env.REACT_APP_API_HOSTNAME ?? 'http://localhost:8000';

const notifyServiceError = (msg: string) => {
  try { window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: msg } })); } catch (e) { /* ignore */ }
};

export type LEStationApiItem = {
  id: number;
  name: string;
  district?: string | null;
  primary_contact?: string | null;
  email?: string | null;
  active?: boolean;
};

export async function getLEStations(page?: number, pageSize?: number, search?: string, active?: boolean): Promise<{ stations: LEStationApiItem[]; meta?: any }> {
  const params = new URLSearchParams();
  if (typeof page === 'number') params.append('page', String(page));
  if (typeof pageSize === 'number') params.append('page_size', String(pageSize));
  if (search) params.append('search', search);
  if (typeof active === 'boolean') params.append('active', String(active));
  const q = params.toString();
  const url = `${HOSTNAME.replace(/\/$/, '')}/adminportal/le-stations${q ? `?${q}` : ''}`;
  const res = await authedFetch(url, { method: 'GET', headers: { ...getAuthHeaders() } });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    const msg = `Failed to fetch LE stations: ${res.status} ${txt}`;
    notifyServiceError(msg);
    throw new Error(msg);
  }
  const body = await res.json().catch(() => null);
  const stations = body?.data?.stations ?? body?.stations ?? [];
  const meta = body?.data?.meta ?? body?.meta ?? null;
  return { stations: Array.isArray(stations) ? stations : [], meta };
}

export async function setLEStationActive(stationId: number, active: boolean): Promise<any> {
  const url = `${HOSTNAME}/adminportal/le-stations/${stationId}/active`;
  const res = await authedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ active }),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const txt = body?.message ?? await res.text().catch(() => '');
    const msg = `Failed to set station active: ${res.status} ${txt}`;
    notifyServiceError(msg);
    throw new Error(msg);
  }
  return body;
}

export async function createLEStation(payload: any): Promise<any> {
  const url = `${HOSTNAME.replace(/\/$/, '')}/adminportal/le-stations/create`;
  const res = await authedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const txt = body?.message ?? await res.text().catch(() => '');
    const msg = `Failed to create LE station: ${res.status} ${txt}`;
    notifyServiceError(msg);
    throw new Error(msg);
  }
  return body;
}

export async function editLEStation(id: number, patch: any): Promise<any> {
  const url = `${HOSTNAME.replace(/\/$/, '')}/adminportal/le-stations/${id}/edit`;
  const res = await authedFetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(patch),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const txt = body?.message ?? await res.text().catch(() => '');
    const msg = `Failed to edit LE station: ${res.status} ${txt}`;
    notifyServiceError(msg);
    throw new Error(msg);
  }
  return body;
}

export async function getLEStationUsers(stationId: number, page?: number, pageSize?: number): Promise<{ users: any[]; meta?: any }> {
  const params = new URLSearchParams();
  if (typeof page === 'number') params.append('page', String(page));
  if (typeof pageSize === 'number') params.append('page_size', String(pageSize));
  const q = params.toString();
  const url = `${HOSTNAME.replace(/\/$/, '')}/adminportal/le-stations/${stationId}/users${q ? `?${q}` : ''}`;
  const res = await authedFetch(url, { method: 'GET', headers: { ...getAuthHeaders() } });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    const msg = `Failed to fetch users for station ${stationId}: ${res.status} ${txt}`;
    notifyServiceError(msg);
    throw new Error(msg);
  }
  const body = await res.json().catch(() => null);
  const users = body?.data?.users ?? body?.users ?? [];
  const meta = body?.data?.meta ?? body?.meta ?? null;
  return { users: Array.isArray(users) ? users : [], meta };
}

export async function deactivateLEStationUser(stationId: number, userId: number | string): Promise<any> {
  const url = `${HOSTNAME.replace(/\/$/, '')}/adminportal/le-stations/${stationId}/users/${userId}/deactivate`;
  const res = await authedFetch(url, { method: 'POST', headers: { ...getAuthHeaders() } });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const txt = body?.message ?? await res.text().catch(() => '');
    const msg = `Failed to deactivate user: ${res.status} ${txt}`;
    notifyServiceError(msg);
    throw new Error(msg);
  }
  return body;
}

/**
 * KAN-129: Set an LE-station user's active state (activate or deactivate).
 * POST /adminportal/le-stations/{stationId}/users/{userId}/active  body: { active }
 */
export async function setLEStationUserActive(stationId: number, userId: number | string, active: boolean): Promise<any> {
  const url = `${HOSTNAME.replace(/\/$/, '')}/adminportal/le-stations/${stationId}/users/${userId}/active`;
  const res = await authedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ active }),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const txt = body?.message ?? await res.text().catch(() => '');
    const msg = `Failed to set user active: ${res.status} ${txt}`;
    notifyServiceError(msg);
    throw new Error(msg);
  }
  return body;
}

export async function createLEStationUser(stationId: number, payload: any): Promise<any> {
  const url = `${HOSTNAME.replace(/\/$/, '')}/adminportal/le-stations/${stationId}/users`;
  const res = await authedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const txt = body?.message ?? await res.text().catch(() => '');
    const msg = `Failed to create user for station ${stationId}: ${res.status} ${txt}`;
    notifyServiceError(msg);
    throw new Error(msg);
  }
  return body;
}
